import React, { FunctionComponent, useCallback, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { styles } from '../../../styles/themes/style'
import { AppRoutes } from '../../../interfaces/store/appStore'
import { useAppActions } from '../../../stores/application/useAppActions'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers'
import OnboardingStepLayout from '../components/OnboardingStepLayout'
import ActionText from '../../../components/ActionText'
import { clearSkippedToAllSet } from '../onboardingExtensionSkipUtils'
import { markMobileOnboardingComplete } from '../onboardingMobileUtils'

const PAGE_TITLE = "You're in! Let's finish on your laptop"

const PAGE_BODY =
    "LeadDelta lives inside your browser as a Chrome extension, and extensions can't be installed on phones or tablets yet. Your account's ready to go. Open LeadDelta on your Chromium browser on a desktop or laptop to install the extension and connect your network."

const MobileFinishOnLaptopSection: FunctionComponent = () => {
    const { setOnboardingStep } = useAppActions()
    const { store: { user } } = useAuthStore()
    const { saveOnboardingData, sendDesktopLinkHandler, saveUserPreferencesHandler } = useAuthHandlers()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [emailLoading, setEmailLoading] = useState(false)

    const finishMobileOnboarding = useCallback(async () => {
        markMobileOnboardingComplete(user?._id)
        clearSkippedToAllSet()
        await saveUserPreferencesHandler({ mobileOnboardingFinished: true })
        await saveOnboardingData({ startedExtensionInstallation: false })
        setOnboardingStep(-2)
        navigate(AppRoutes.HOME, { replace: true })
    }, [user?._id, saveUserPreferencesHandler, saveOnboardingData, setOnboardingStep, navigate])

    const handleEmailLink = useCallback(async () => {
        if (emailLoading || loading) return
        setEmailLoading(true)
        try {
            await sendDesktopLinkHandler('onboarding')
        } finally {
            setEmailLoading(false)
        }
    }, [emailLoading, loading, sendDesktopLinkHandler])

    const handleGoToWorkspace = useCallback(() => {
        if (loading) return
        setLoading(true)
        void finishMobileOnboarding().finally(() => setLoading(false))
    }, [loading, finishMobileOnboarding])

    return (
        <OnboardingStepLayout activeStepIndex={1} title={PAGE_TITLE} showStepper={false}>
            <FinishCard>
                <BodyText>{PAGE_BODY}</BodyText>
                <ActionsGroup>
                    <ActionText
                        onClickHandler={() => void handleEmailLink()}
                        $disabled={emailLoading || loading}
                        $regular
                    >
                        Email me the desktop link
                    </ActionText>
                    <PrimaryButton type="button" disabled={loading} onClick={handleGoToWorkspace}>
                        Go to my workspace
                    </PrimaryButton>
                </ActionsGroup>
            </FinishCard>
        </OnboardingStepLayout>
    )
}

export default MobileFinishOnLaptopSection

const FinishCard = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 24px 24px;
    gap: 32px;
    width: 100%;
    border: 1px solid ${styles.colors.black200};
    border-radius: 12px;
    background: ${styles.colors.white};
`

const BodyText = styled.p`
    margin: 0;
    width: 100%;
    font-family: NunitoSans;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    color: ${styles.colors.black400};
`

const ActionsGroup = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
`

const PrimaryButton = styled.button`
    box-sizing: border-box;
    width: 100%;
    min-height: 52px;
    padding: 0 24px;
    border-radius: 12px;
    border: 1px solid ${styles.colors.primary600};
    background-color: ${styles.colors.primary600};
    color: ${styles.colors.white};
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover:not(:disabled) {
        background-color: ${styles.colors.primary400};
        border-color: ${styles.colors.primary400};
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    &:focus {
        outline: none;
    }
`
